import path from "path";
import { existsSync } from "fs";

import { pathExists } from "fs-extra";
import pLimit from "p-limit";

import { FormGlobs } from "../../../../Commons/FormGlobsUtils";
import { globAsync } from "../../../../Commons/FsUtils";
import { reject } from "../../../../../../Common/TypingUtils";
import { extractGfvsFromFileName } from "../../../../Commons/ExtractGfvsFromFileName";
import { WellKnownDirectories } from "../../../../../../Common/WellKnownDirectories";
import { FormLocationInfo } from "../../../../Commons/FormLocationInfo";

export class FarmDirectory {
    private readonly farmDirectory: string;
    private gfvToPathMap: undefined | Map<string, string>;
    private allGfvInfos?: Array<{ gfv: string; formPath: string; formRelativePath: string }>;
    private readonly ensureGfvDirectoriesUpdated: () => Promise<void>;

    public constructor(farmDirectory: string) {
        this.farmDirectory = farmDirectory;
        const updateGfvDirectoriesLimit = pLimit(1);
        this.ensureGfvDirectoriesUpdated = async () => {
            if (this.allGfvInfos == undefined || this.gfvToPathMap == undefined) {
                await updateGfvDirectoriesLimit(async () => {
                    if (this.allGfvInfos == undefined || this.gfvToPathMap == undefined) {
                        await this.updateGfvDirectories();
                    }
                });
            }
        };
        setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            updateGfvDirectoriesLimit(() => this.updateGfvDirectories());
        }, 60000);
    }

    private async updateGfvDirectories(): Promise<void> {
        this.allGfvInfos = await FormGlobs.enumerateFormsInRepository(path.join(this.farmDirectory, ".."));
        // @ts-ignore
        this.gfvToPathMap = new Map(this.allGfvInfos.map(x => [x.gfv, x.formPath]));
    }

    public async getAllPicklists(): Promise<string[]> {
        return await globAsync("*", { cwd: path.join(this.farmDirectory, "picklists") });
    }

    public async getFormDirectory(): Promise<string[]> {
        return await globAsync("*", { cwd: path.join(this.farmDirectory, "picklists") });
    }

    private async enumerateAllGfvs(): Promise<Array<{ gfv: string; formPath: string; formRelativePath: string }>> {
        await this.ensureGfvDirectoriesUpdated();
        return this.allGfvInfos ?? reject();
    }

    private async getGfvToPathMap(): Promise<Map<string, string>> {
        await this.ensureGfvDirectoriesUpdated();
        return this.gfvToPathMap ?? reject();
    }

    public async getAllGfvs(): Promise<string[]> {
        const formGfvPaths = await this.enumerateAllGfvs();
        return formGfvPaths.map(x => x.gfv);
    }

    public async getAllGfvWithLocations(): Promise<FormLocationInfo[]> {
        const formGfvPaths = await this.enumerateAllGfvs();
        return formGfvPaths;
    }

    public async getFormJsonPath(gfv: string): Promise<string | undefined> {
        await this.ensureGfvDirectoriesUpdated();
        const formDirPath = (this.gfvToPathMap ?? reject()).get(gfv);
        if (formDirPath != undefined && existsSync(path.join(formDirPath, WellKnownDirectories.FormJsonFileName))) {
            return path.join(formDirPath, WellKnownDirectories.FormJsonFileName);
        }
        return undefined;
    }

    public async getAllConverterFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("converters/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getConverterFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        const gfvToPathMap = await this.getGfvToPathMap();
        for (const gfv of extractGfvsFromFileName(filename)) {
            const formPath = gfvToPathMap.get(gfv);
            if (formPath == undefined) {
                continue;
            }
            const filePath = path.join(formPath, "converters", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllSchemasFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("schemas/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getSchemaFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "schemas", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllNormalizeFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("normalize/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getNormalizeFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "normalize", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllUnmapFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("unmap/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getUnmapFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "unmap", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllMapFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("map/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getMapFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "map", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllPatchFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("patch/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getPatchFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "patch", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public async getAllMergersFilePaths(): Promise<string[]> {
        const result = [];
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            result.push(...(await globAsync("mergers/*", { cwd: gfvInfo.formPath })));
        }
        return result;
    }

    public async getMergersFilePathByFileNameOrUndefined(filename: string): Promise<string | undefined> {
        for (const gfvInfo of await this.enumerateAllGfvs()) {
            const filePath = path.join(gfvInfo.formPath, "mergers", filename);
            if (await pathExists(filePath)) {
                return filePath;
            }
        }
        return undefined;
    }

    public resetAllGfvs() {
        this.allGfvInfos = undefined;
        this.gfvToPathMap = undefined;
    }
}
